import React, { useState } from "react";
import { TextField, IconButton, InputAdornment, FormControlLabel, Typography, Checkbox } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Cta } from '../components/Cta.js';
import { ReactComponent as Check } from "../assets/loginPage/check.svg";
import { ReactComponent as Briefcase } from "../assets/loginPage/briefcase.svg";
import { ReactComponent as Group31 } from "../assets/loginPage/group-31.svg";
import { ReactComponent as Mail } from "../assets/loginPage/mail.svg";
import maskGroup from "../assets/loginPage/mask-group.png";
import { ReactComponent as Profile } from "../assets/loginPage/profile.svg";
import "../css/SignUpPage.css";
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { createUserWithEmailAndPassword, updateProfile, signOut, sendEmailVerification, signInWithEmailAndPassword } from "firebase/auth";
import { StyledEngineProvider } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";




/* 
Sign up is basically done. Can be improved based on the following TODOs.

TODO: 
- edit password requirements in firebase console (currently set to 6 characters)
- update password requirements to show in the frontend (i.e. "Password must be at least 6 characters long.")
- add checking for if someone signs up with an email that already exists in the system?
*/



const SignUpPage = () => {
    const navigate = useNavigate();

    // min length for password is 6 characters - need to add this info to the frontend to tell users 
    // can update password requirements in Authentication settings in Firebase console
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        name: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [agreedToGuidelines, setAgreedToGuidelines] = useState(false);
    const [guidelinesError, setGuidelinesError] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const BASE_URL = window.location.hostname === 'localhost'
        ? 'http://localhost:3000'
        : 'https://joynet-99b18.web.app';
        
    const actionCodeSettings = {
        // URL you want to redirect back to. The domain (www.example.com) for this
        // URL must be in the authorized domains list in the Firebase Console.
        // todo correct this to the production url
        url: `${BASE_URL}/login?verified=true`,
        handleCodeInApp: false
    }; 

    const handleSubmit = async (e) => {
        e.preventDefault();
    
        if (!agreedToGuidelines) {
            setGuidelinesError(true);
            return;
        }
        
        try {

            // Create the user with email and password
            const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
            
            // Update the user's profile with the display name
            await updateProfile(userCredential.user, {
                displayName: formData.name,
            });
            await sendEmailVerification(userCredential.user, actionCodeSettings);
            console.log("Email sent successfully");
            // log out the user
            await signOut(auth);
            createPopup(`An authentication email has been sent to ${formData.email}. You must verify your email before logging in.`, 'success');
        } catch (err) {
            let errorMessage = "Something went wrong. Please try again.";
            if (err.code === 'auth/email-already-in-use') {
                errorMessage = "An account with this email already exists. Please log in.";
            } else if (err.code === 'auth/invalid-email') {
                errorMessage = "The email address is not valid.";
            } else if (err.code === 'auth/weak-password') {
                errorMessage = "Password should be at least 6 characters.";
            } else {
                errorMessage = "Something went wrong. Please try again.";
            }
            setError(errorMessage);
            createPopup(errorMessage, 'error');   
        }
    };   

    const createPopup = (messageText, type = 'success') => {
        const popup = document.createElement('div');
        popup.className = `popup ${type}`;
        
        const closeBtn = document.createElement('button');
        closeBtn.className = 'popup-close';
        closeBtn.innerHTML = '×';
        closeBtn.onclick = () => popup.remove();
        
        const message = document.createElement('div');
        message.className = 'popup-message';
        message.innerHTML = messageText;
        
        popup.appendChild(closeBtn);
        popup.appendChild(message);
        document.body.appendChild(popup);

        setTimeout(() => {
            popup.remove();
        }, 4000);
    };

    return (
        <StyledEngineProvider injectFirst>
        <CssBaseline />
        <div className="auth-page">
            <div className="left-panel">
                <div className="logo-container">
                    <Group31 className="group responsive-img" />
                    <div>
                        <span className="joynet-text">JoyNet</span>
                    </div>
                </div>

                <Typography variant="subtitle2" color="primary" className="tagline">
                    WELCOME TO YOUR SPACE OF HEALING AND JOY
                </Typography>
                <Typography variant="h4" className="title-text">
                    Join JoyNet Where Every Heart Finds a Journey to Joy
                </Typography>
                <Box
                    component="form"
                    className="signup-form"
                    onSubmit={handleSubmit}
                >
                    <TextField
                        name="name"
                        label="Name"
                        variant="filled"
                        value={formData.name}
                        onChange={handleChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Profile className="input-icon" />
                                </InputAdornment>
                            ),
                            disableUnderline: true
                        }}
                    />
                    <TextField
                        name="email"
                        label="Email"
                        variant="filled"
                        value={formData.email}
                        onChange={handleChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Mail className="input-icon" />
                                </InputAdornment>
                            ),
                            disableUnderline: true
                        }}
                    />

                    <TextField
                        name="password"
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        variant="filled"
                        value={formData.password}
                        onChange={handleChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Briefcase className="input-icon" />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={handleClickShowPassword}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                            disableUnderline: true
                        }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={agreedToGuidelines}
                                onChange={(e) => {
                                    setAgreedToGuidelines(e.target.checked);
                                    setGuidelinesError(false);
                                }}
                                color = "primary"
                            />
                        }
                        label={
                            <Typography 
                                variant="body2" 
                                color={guidelinesError ? 'error' : 'text.primary'}
                            >
                                I agree to the{' '}
                                <a 
                                    href="https://www.joynet.app/community-guideline" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                >
                                    Community Guidelines
                                </a>
                            </Typography>
                        }
                    />
                    {guidelinesError && (
                        <Typography 
                            variant="caption" 
                            color="error" 
                        >
                            Please accept the Community Guidelines to continue
                        </Typography>
                    )}
                    
                    <Cta text="Sign Up" type="submit" onClick={handleSubmit} />

                    <Typography variant="body2" className="sign-up-text">
                        Already have an account?&nbsp;
                        <span className="login-link" onClick={() => navigate('/login')}>
                            Log In
                        </span>
                    </Typography>
                </Box>
            </div>

            <div className="right-panel">
                <div className="benefit-card">
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Check className="benefit-icon" />
                        <div>
                            <div className="benefit-title">Begin Your Journey to Healing</div>
                            <div className="benefit-description">
                                Find solace and connection in our community. The JoyNet App is a sanctuary where conversations heal and uplift.
                            </div>
                        </div>
                    </div>
                </div>

                <div className="benefit-card">
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Check className="benefit-icon" />
                        <div>
                            <div className="benefit-title">Simplicity in Connection, Richness in Experience</div>
                            <div className="benefit-description">
                                Signing up is just the start. Join chatrooms, connect with kindred spirits, and share in the celebration of Black Joy.
                            </div>
                        </div>
                    </div>
                </div>
                <img src={maskGroup} alt="Illustration" className="auth-image" />
            </div>
        </div>
        </StyledEngineProvider>
    );
};

export default SignUpPage;














